import React, { useEffect, useState } from "react";
import { Button } from "../ui/Button";
import { GenericBtn } from "pages/finance/v2/FinanceRequest";
import CurrencyToggle from "../currencyToggle";
import { SearchInput as InputField } from "../TableSearchInput";
import QuestionHelpIcon from "../QuestionHelpIcon";
import { RadioGroup, RadioGroupItem } from "../ui/Radio";
import { formatMoney } from "utilities/formatMoney";
import { checkFinanceOption, EMAIL_REGEX, isCrypto } from "constants/global";
import { useMutation, useQuery } from "react-query";
import {
  fetchSingleOrder,
  handleFetchInboundOrders,
  handleGetAllInvoice,
  handleGetSingleInvoice,
  handleUploadFileUtils,
} from "api/fetchers";
import UploadDocModal from "../form/uploadDocModal";
import {
  createInventoryFinance,
  createPayableFinance,
  createPaymentFinance,
  createReceivableFinance,
} from "api/credit-fetchers";
import useUserDetails from "hooks/useUserDetails";
import { toast } from "react-toastify";
import { useLocation, useNavigate } from "react-router-dom";
import FinanceTopNav from "pages/finance/v2/FinanceTopNav";
import FinanceSideNav from "pages/finance/v2/FinanceSideNav";
import InventoryFinanceForm from "./InventoryFinanceForm";
import {
  fileTypeToUpload,
  MINIMUM_AMOUNT,
  getOrderToDisplay,
  managementFinanceOptions,
  radioFinanceOptions,
  MAXIMUM_AMOUNT,
  displayCurrencyAndAmount,
  CRYPTO_MINIMUM_AMOUNT,
} from "constants/FinanceConstants";
import ReceivableFinanceForm from "./ReceivableFinanceForm";
import SuccessModal from "../SuccessModal";
import Loading from "../loading";
import { unformat } from "@react-input/number-format";
import PaymentFinance from "./PaymentFinance";
import currencyPairCheck from "utilities/currencyPairCheck";

const PayableFinance = ({ goBack, paymentMethod, setFileKey, hasProcessingFinance, hasHover }) => {
  const [show, setShow] = useState(false);
  const [selectedOption, setSelectedOption] = useState(radioFinanceOptions[0].value);
  const [selectManagementOption, setSelectManagementOption] = useState(
    managementFinanceOptions[0].value
  );
  const [requestedAmount, setRequestedAmount] = useState("");
  const [buyBackAmount, setBuyBackAmount] = useState("");
  const [buyBackPeriod, setBuyBackPeriod] = useState("");
  const [duration, setDuration] = useState("");
  const [orderDimensionsList, setOrderDimensionsList] = useState([]);
  const [_, setSelectedUser] = useState();
  const [addDocModal, setAddDocModal] = useState(null);
  const [contractAgreement, setContractAgreement] = useState(null);
  const [freightDocument, setFreightDocument] = useState(null);
  const [transactionDocument, setTransactionDocument] = useState(null);
  const { business, businessId, currency, newWalletsData,
    isLoadingNewWallets,
    refetchNewWallets } = useUserDetails((state) => state);
  const [productCategory, setProductCategory] = useState("");
  const [productType, setProductType] = useState("");
  const [wareHouseLocation, setWareHouseLocation] = useState("");
  const [province, setProvince] = useState("");
  const navigate = useNavigate();
  const { state: initialData } = useLocation();
  const [showSuccess, setShowSuccessModal] = useState(false);
  const [reason, setReason] = useState("");
  const [paymentAgreement, setPaymentAgreement] = useState('')
  const [network, setNetwork] = useState('')
  const [uniqueWallet, setUniqueWallet] = useState('')
  const [walletTouched, setWalletTouched] = useState(false);
  const [swiftRefTouched, setSwiftRefTouched] = useState(false);
  const [interest, setInterest] = useState('')
  const [swiftRef, setSwiftRef] = useState('')
  const [selectedToken, setSelectedToken] = useState('')
  const [paymentAgreementDoc, setPaymentAgreementDoc] = useState(null)
  const [bankName, setBankName] = useState('')
  const [contactPerson, setContactPerson] = useState('')
  const [contactEmail, setContactEmail] = useState('')
  const [payoutDestinationType, setPayoutDestinationType] = useState('CreditWallet')

  const isBusinessApproved = business?.status === "APPROVED";
  const isPayableFinanceForm = paymentMethod === checkFinanceOption("Payables finance");
  const isReceivableForm = paymentMethod === checkFinanceOption("Receivables finance");
  const isPaymentFinanceForm = paymentMethod === checkFinanceOption("Payment finance");
  const isInventoryFinanceForm = paymentMethod === checkFinanceOption("Inventory finance");
  const { data, isLoading: isLoadingPo } = useQuery({
    queryKey: ["get-completed-orders", currency],
    queryFn: async () => handleFetchInboundOrders({ currency, sortBy: "newest-first" }),
  });
  const MaxAmount = 6250000;
  const confirmedPOForFinance = data?.inbound?.filter(
    (order) => order.orderStatus === "CONFIRMED" && order.totalAmount >= MaxAmount
  );
  const { data: invoiceList, isLoading: isLoadingInvoice } = useQuery({
    queryKey: ["alInvoice", businessId],
    queryFn: () =>
      handleGetAllInvoice({
        businessId: businessId,
        currency,
        type: "outbound",
        period: "all",
        sortBy: "newest-first",
      }),
    enabled: !!businessId,
  });
  const confirmedInvoiceForFinance = invoiceList?.inbound?.filter(
    (order) =>
      (order?.paymentStatus === "UNPAID" || order?.paymentStatus === "DUE") &&
      order.totalAmount >= MaxAmount
  );

  const handleDelete = (id) => {
    setOrderDimensionsList((prev) => prev.filter((item) => item?._id !== id));
  };
  const { data: orderData, isLoading: fetchingOrderLoading } = useQuery({
    queryFn: () => {
      const orderId = orderDimensionsList?.[0]?._id;
      return isReceivableForm
        ? handleGetSingleInvoice({ invoiceId: orderId, businessId, currency })
        : fetchSingleOrder(orderId);
    },
    enabled: !!orderDimensionsList?.length,
  });
  const { mutateAsync: uploadDocumentFunc, isLoading: uploadingDocsLoading } = useMutation({
    mutationFn: ({ selectedFile, name }) => {
      return handleUploadFileUtils({ selectedFile, name });
    },
  });
  const { mutateAsync: handleCreatePayableFinance, isLoading: creatingPayableFinanceLoading } =
    useMutation({
      mutationFn: (data) => {
        return createPayableFinance({ businessId, data, financeId: initialData?._id });
      },
    });
  const {
    mutateAsync: handleReceivablePayableFinance,
    isLoading: creatingReceivableFinanceLoading,
  } = useMutation({
    mutationFn: (data) => {
      return createReceivableFinance({ businessId, data, financeId: initialData?._id });
    },
  });

  const {
    mutateAsync: handleInventoryPayableFinance,
    isLoading: creatingInventoryFinanceLoading,
  } = useMutation({
    mutationFn: (data) => {
      return createInventoryFinance({ businessId, data });
    },
  });

  const {
    mutateAsync: handlePaymentFinance,
    isLoading: creatingPaymentFinanceLoading,
  } = useMutation({
    mutationFn: (data) => {
      return createPaymentFinance({ businessId, data, financeId: initialData?._id });
    },
  });

  const uploadInventoryData = (data, type) => {
    setRequestedAmount(data?.requestedAmount || data?.paymentFinance?.requestedAmount || "");
    setOrderDimensionsList(data?.purchaseOrder);
    setSelectManagementOption(data?.managementType);
    if (type === "receivable_finance") {
      setSelectedUser({
        name: initialData?.receivableFinance?.contractEmployer?.businessName,
        _id: data?.receivableFinance?.contractEmployer._id,
        personalName: `${data?.receivableFinance?.contractEmployer?.firstName ?? ""} ${data?.receivableFinance?.contractEmployer?.lastName ?? ""
          }`,
        ...data?.receivableFinance?.contractEmployer,
      });
      if (data?.receivableFinance?.invoices?.length) {
        const selectedInvoice = confirmedInvoiceForFinance?.find((po) => {
          if (typeof data?.receivableFinance?.invoices[0] == "string")
            return po?._id === data?.receivableFinance?.invoices[0];
          return po?._id === data?.receivableFinance?.invoices[0]?._id;
        });
        setOrderDimensionsList([selectedInvoice]);
      }
    }
    if (type === "inventory_finance") {
      setSelectedUser({
        name: data?.inventoryFinance?.contractEmployer?.businessName,
        _id: data?.inventoryFinance?.contractEmployer?._id,
        personalName: `${data?.inventoryFinance?.contractEmployer?.firstName ?? ""} ${data?.inventoryFinance?.contractEmployer?.lastName ?? ""
          }`,
        ...data?.inventoryFinance?.contractEmployer,
      });
      setProductCategory(data?.inventoryFinance?.productCategory);
      setProductType(data?.inventoryFinance?.productType);
      setWareHouseLocation(data?.inventoryFinance?.warehouseCountry);
      setProvince(data?.inventoryFinance?.warehouseState);
      setBuyBackAmount(data?.inventoryFinance?.buyBackAmount);
    }
    if (type === "payable_finance") {
      if (data?.payableFinance?.financeCategory) {
        setSelectedOption(data?.payableFinance?.financeCategory);
      }
      setSelectedUser({
        name: data?.payableFinance?.contractEmployer?.businessName,
        _id: data?.payableFinance?.contractEmployer?._id,
        personalName: `${data?.payableFinance?.contractEmployer?.firstName ?? ""} ${data?.payableFinance?.contractEmployer?.lastName ?? ""
          }`,
        ...data?.payableFinance?.contractEmployer,
      });
      setContractAgreement({
        ...data?.payableFinance?.uploadDocuments?.contractAgreement,
        url: data?.payableFinance?.uploadDocuments?.contractAgreement?.url,
        name: data?.payableFinance?.uploadDocuments?.contractAgreement?.key,
      });
      if (data?.payableFinance?.freightPaymentUrl) {
        setFreightDocument({
          ...data?.payableFinance?.freightPaymentUrl,
          url: data?.payableFinance?.freightPaymentUrl,
          name: data?.payableFinance?.freightPaymentUrl,
        });
      }
      if (data?.payableFinance?.transactionUrl) {
        setTransactionDocument({
          ...data?.payableFinance?.transactionUrl,
          url: data?.payableFinance?.transactionUrl,
          name: data?.payableFinance?.transactionUrl,
        });
      }

      if (data?.payableFinance?.purchaseOrders?.length) {
        const selectedPo = confirmedPOForFinance?.find((po) => {
          if (typeof data?.payableFinance?.purchaseOrders[0] == "string")
            return po?._id === data?.payableFinance?.purchaseOrders[0];
          return po?._id === data?.payableFinance?.purchaseOrders[0]?._id;
        });
        setOrderDimensionsList([selectedPo]);
      }
    }
    if (type === "payment_finance") {
      const paymentData = data?.paymentFinanceData?.[0] || data?.paymentFinance || {};
      setReason(paymentData?.purposeOfFund || "");
      setDuration(paymentData?.duration || "");
      setInterest(paymentData?.interestRate || "");
      setPaymentAgreement(paymentData?.paymentReferenceType || "");
      if (paymentData?.contractAgreement) {
        setContractAgreement(paymentData?.contractAgreement);
      }
      if (paymentData?.swiftReference) {
        setSelectedToken(paymentData?.swiftReference?.swiftType);
        setPaymentAgreementDoc(paymentData?.swiftReference?.link);
        setBankName(paymentData?.swiftReference?.bankName || "");
        setContactPerson(paymentData?.swiftReference?.contactPerson || "");
        setContactEmail(paymentData?.swiftReference?.contactEmail || "");
      }
      if (paymentData?.swiftRef) {
        setSwiftRef(paymentData?.swiftRef);
      }
      if (paymentData?.swiftReference?.uetr || paymentData?.swiftReference?.UETR) {
        setSwiftRef(paymentData?.swiftReference?.UETR || paymentData?.swiftReference?.uetr || '');
      }
      if (paymentData?.paymentDestination) {
        if (paymentData?.payoutDestinationType) {
          setPayoutDestinationType(paymentData?.payoutDestinationType);
        } else if (paymentData?.paymentDestination?.address === "Credit wallet") {
          setPayoutDestinationType("CreditWallet");
        } else if (paymentData?.paymentDestination?.address) {
          setPayoutDestinationType("ExternalAccount");
        } else {
          setPayoutDestinationType("CreditWallet");
        }
        setUniqueWallet(paymentData?.paymentDestination?.address === "Credit wallet" ? "" : (paymentData?.paymentDestination?.address || ""));
        setNetwork(paymentData?.paymentDestination?.network === "N/A" ? "" : (paymentData?.paymentDestination?.network || ""));
      } else {
        setPayoutDestinationType("CreditWallet");
      }
    }
  };

  useEffect(() => {
    if (initialData && initialData?.financeType === "receivable_finance") {
      uploadInventoryData(initialData, "receivable_finance");
    }
    if (initialData && initialData?.financeType === "payable_finance") {
      uploadInventoryData(initialData, "payable_finance");
    }
    if (initialData && initialData?.financeType === "inventory_finance") {
      uploadInventoryData(initialData, "inventory_finance");
    }
    if (initialData && initialData?.financeType === "payment_finance") {
      uploadInventoryData(initialData, "payment_finance");
    }
  }, [isLoadingPo, isLoadingInvoice]);

  const isDraft = initialData?._id || initialData?.status === "draft";

  const addFilesToState = (fileType, response) => {
    switch (fileType) {
      case fileTypeToUpload.Contract:
        return setContractAgreement(response);
      case fileTypeToUpload.Freight:
        return setFreightDocument(response);
      case fileTypeToUpload.Transaction:
        return setTransactionDocument(response);
      default:
        return undefined;
    }
  };
  const isValidNumberString = (value) => {
    return value !== "" ? true : !isNaN(parseFloat(value)) && isFinite(value);
  };

  function getEightyPercent(amount) {
    if (typeof amount !== "number" || isNaN(amount)) {
      return 0;
    }
    return amount * 0.8;
  }
  const maxAmount = getEightyPercent(Number(orderDimensionsList?.[0]?.totalAmount));

  const unformattedRequestedAmount = Number(unformat(requestedAmount));

  const paymentFinanceMinimumAmount = process.env.REACT_APP_STAGE === "production" ? 50000 : 1;


  const MINIMUM_AMOUNT_TO_USE = isCrypto(currency) ? CRYPTO_MINIMUM_AMOUNT : MINIMUM_AMOUNT;

  const minimumRequestedAmount = isPaymentFinanceForm
    ? paymentFinanceMinimumAmount
    : MINIMUM_AMOUNT_TO_USE;

  const isAmountValid = unformattedRequestedAmount > 0 ? (unformattedRequestedAmount < MINIMUM_AMOUNT_TO_USE || unformattedRequestedAmount > maxAmount) : false;

  const inventoryFinanceValidation = requestedAmount?.length
    ? isValidNumberString(requestedAmount) &&
    Number(unformat(requestedAmount)) >= minimumRequestedAmount
    : !requestedAmount?.length;
  const isBuyBackValid = buyBackAmount?.length
    ? Boolean(
      isValidNumberString(buyBackAmount) &&
      Number(unformat(buyBackAmount)) > unformattedRequestedAmount
    )
    : !buyBackAmount?.length;

  const handleSubmit = async (isDraft = false) => {
    const supplier = getOrderToDisplay({
      businessId,
      data: isReceivableForm ? orderData : orderData?.data,
      isPayableFinance: paymentMethod === checkFinanceOption("Payables finance"),
    });
    try {
      const {
        firstName,
        lastName,
        phoneNumber,
        email,
        businessName,
        businessNature,
        businessAddress,
      } = supplier ?? {};
      const payload = {
        currency,
        financeCategory: selectedOption,
        requestedAmount: Number(unformat(requestedAmount)),
        contractAgreement: contractAgreement?.url,
        isDraft,
        managementType: selectManagementOption,
        managementFeeDebitWalletType: selectManagementOption === 'fromLoan' ? 'FINANCE' : 'TRADEVU',
      };
      if (selectedOption === radioFinanceOptions[0].value) {
        payload["purchaseOrderIds"] = orderDimensionsList.map((item) => item?._id);
        payload["contractEmployer"] = {
          firstName,
          lastName,
          phoneNumber,
          email,
          businessName,
          businessNature,
          businessAddress,
        };
      }
      if (selectedOption === radioFinanceOptions[1].value) {
        payload["transactionUrl"] = transactionDocument?.url;
      }
      if (selectedOption === radioFinanceOptions[2].value) {
        payload["freightPaymentUrl"] = freightDocument?.url;
      }
      const receivablePayload = {
        currency,
        invoiceIds: orderDimensionsList.map((item) => item?._id),
        contractEmployer: {
          firstName,
          lastName,
          phoneNumber,
          email,
          businessName,
          businessNature,
          businessAddress,
        },
        requestedAmount: Number(unformat(requestedAmount)),
        contractAgreement: contractAgreement?.url,
        isDraft,
        managementType: selectManagementOption,
      };

      let res;
      if (isPayableFinanceForm) {
        res = await handleCreatePayableFinance(payload);
      }
      if (isReceivableForm) {
        res = await handleReceivablePayableFinance(receivablePayload);
      }
      if (res.status !== 400) {
        toast.success(res?.message || "Payable finance created successfully");
        navigate("/finance/credit");
      } else {
        toast.error(
          typeof res?.response?.data?.message !== "string"
            ? res?.response?.data?.message?.at(-1)
            : res?.response?.data?.message || "Payable finance created successfully"
        );
      }
    } catch (error) {
      console.log({ error }, "error while submission");
    }
  };

  const handleSubmitForInventory = async (isDraft = false) => {
    const isInventoryPayload = {
      currency,
      requestedAmount: Number(unformat(requestedAmount)),
      productCategory,
      productType,
      warehouseCountry: wareHouseLocation,
      warehouseState: province,
      buyBackCurrency: currency,
      buyBackAmount,
      buyBackPeriod: buyBackPeriod.split(" ")[0],
      isDraft,
      managementType: selectManagementOption,
    };
    const res = await handleInventoryPayableFinance(isInventoryPayload);
    if (res.status !== 400) {
      toast.success(res?.message || "Payable finance created successfully");
      navigate("/finance/credit");
    } else {
      toast.error(
        typeof res?.response?.data?.message !== "string"
          ? res?.response?.data?.message?.at(-1)
          : res?.response?.data?.message || "Payable finance created successfully"
      );
    }
  };

  const handleSubmitForPaymentFinance = async (isDraft = false) => {
    try {
      const paymentFinanceData = {
        currency,
        requestedAmount: Number(unformat(requestedAmount)),
        purposeOfFund: reason,
        duration: Number(duration),
        interestRate: Number(interest),
        paymentReferenceType: paymentAgreement,
        contractAgreement,
        disburseMethod: payoutDestinationType,
        isDraft,
      };
      if (payoutDestinationType === "ExternalAccount") {
        paymentFinanceData.paymentDestination = {
          address: uniqueWallet,
          network
        }
      }
      if (paymentAgreement === "contractAgreement") {
        paymentFinanceData.contractAgreement = {
          name: contractAgreement.name,
          url: contractAgreement.url,
          type: "application/pdf",
        };
      }
      if (paymentAgreement === "swiftReference") {
        paymentFinanceData.swiftReference = {
          swiftType: selectedToken,
          bankName,
          contactPerson,
          contactEmail,
        };
        if (selectedToken === "SWIFT") {
          paymentFinanceData.swiftReference.link = {
            name: paymentAgreementDoc?.name,
            url: paymentAgreementDoc?.url,
            type: "application/pdf",
          };
        }
        if (swiftRef) {
          paymentFinanceData.swiftReference[selectedToken.toLowerCase()] = swiftRef
        }
      }
      const res = await handlePaymentFinance(paymentFinanceData);
      if (res.status !== 400) {
        toast.success(res?.message || "Payable finance created successfully");
        navigate("/finance/credit");
      } else {
        toast.error(
          typeof res?.response?.data?.message !== "string"
            ? res?.response?.data?.message?.at(-1)
            : res?.response?.data?.message || "Payable finance created successfully"
        );
      }
    } catch (error) {
      console.log({ error })
    }
  };

  const isValidEVMAddress = (address) =>
    /^0x[a-fA-F0-9]{40}$/.test(address) || /^T[1-9A-HJ-NP-Za-km-z]{33}$/.test(address);
  const isValidUetr = (value) =>
    /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(
      String(value || "").trim()
    );
  const isWalletInvalid = Boolean(uniqueWallet && walletTouched && !isValidEVMAddress(uniqueWallet));
  const isUetrInvalid = Boolean(
    paymentAgreement === "swiftReference" &&
    selectedToken === "UETR" &&
    swiftRefTouched &&
    swiftRef &&
    !isValidUetr(swiftRef)
  );

  const isLoading = creatingPayableFinanceLoading || creatingReceivableFinanceLoading;

  const trnxAndFreightSelected =
    selectedOption === radioFinanceOptions[1].value ||
    selectedOption === radioFinanceOptions[2].value;

  const isTrnxAndFrieghtValid = unformattedRequestedAmount > 0 ? (unformattedRequestedAmount >= MINIMUM_AMOUNT_TO_USE &&
    unformattedRequestedAmount <= MAXIMUM_AMOUNT) : true;

  const isPoFormValid = Boolean(
    !isAmountValid && contractAgreement && !!orderDimensionsList.length
  );
  const isTransactionFormValid = Boolean(
    isTrnxAndFrieghtValid && contractAgreement && transactionDocument
  );
  const isFreightFormValid = Boolean(
    isTrnxAndFrieghtValid && contractAgreement && freightDocument
  );
  const isFormValid =
    selectedOption === radioFinanceOptions[0]?.value
      ? isPoFormValid
      : selectedOption === radioFinanceOptions[1].value
        ? isTransactionFormValid
        : isFreightFormValid;

  const isInventoryFormValid = Boolean(
    isAmountValid &&
    !!productCategory &&
    !!productType &&
    !!province &&
    !!wareHouseLocation &&
    !!isBuyBackValid &&
    !!buyBackPeriod
  );

  const isValidEmail = (email) => EMAIL_REGEX.test(String(email || "").trim());

  const isPaymentFinanceFormValid = Boolean(
    requestedAmount &&
    unformattedRequestedAmount >= paymentFinanceMinimumAmount &&
    reason &&
    reason.length <= 300 &&
    duration &&
    Number(duration) <= 3 &&
    interest &&
    paymentAgreement &&
    (paymentAgreement === "contractAgreement"
      ? contractAgreement
      : (selectedToken === "SWIFT" || selectedToken === "UETR")
        ? (bankName &&
          contactPerson &&
          contactEmail &&
          isValidEmail(contactEmail) &&
          (selectedToken === "SWIFT"
            ? paymentAgreementDoc
            : swiftRef && isValidUetr(swiftRef)))
        : false) &&
    (payoutDestinationType === "CreditWallet"
      ? true
      : (uniqueWallet &&
        isValidEVMAddress(uniqueWallet) &&
        !isWalletInvalid &&
        !isUetrInvalid &&
        network))
  );

  return (
    <>
      {(isLoadingPo || isLoadingInvoice) && (
        <div className="fixed left-0 top-0 h-screen w-screen z-[1000000] opacity-65">
          <Loading />
        </div>
      )}
      <FinanceTopNav
        navigate={navigate}
        handleSaveAsDraft={() =>
          isPaymentFinanceForm
            ? handleSubmitForPaymentFinance(true)
            : isInventoryFinanceForm
              ? handleSubmitForInventory(true)
              : handleSubmit(true)
        }
        onSubmit={() =>
          isPaymentFinanceForm
            ? handleSubmitForPaymentFinance()
            : isInventoryFinanceForm
              ? handleSubmitForInventory()
              : handleSubmit()
        }
        paymentMethod={paymentMethod}
        isValid={
          hasProcessingFinance
            ? !hasProcessingFinance
            : isPaymentFinanceForm
              ? isPaymentFinanceFormValid
              : isInventoryFinanceForm
                ? isInventoryFormValid
                : isReceivableForm
                  ? isPoFormValid
                  : isFormValid
        }
        isLoading={
          isLoading ||
          creatingInventoryFinanceLoading ||
          creatingPaymentFinanceLoading
        }
        hasHover={hasHover}
      />
      <main className="flex flex-col w-full h-full md:flex-row">
        <FinanceSideNav handleBack={goBack} paymentMethod={paymentMethod} />
        <section className="w-full pb-10 overflow-y-auto md:pl-10 scroll-smooth">
          <section className="md:w-[594px] w-11/12 pt-10 pb-20 md:ml-20 ml-auto sm:mx-0 mr-auto">
            <div className="text-left">
              <h1 className="font-[450] text-gray-10 text-2xl">{paymentMethod}</h1>
              <p className="font-light md:text-base text-gray-10">
                Fill required details for this request
              </p>
            </div>

            <div className="mt-6 space-y-6">
              {(paymentMethod === checkFinanceOption("Inventory finance") ||
                paymentMethod == checkFinanceOption("Payment finance")) && (
                  <div>
                    <label className="mb-1 text-sm text-gray-70">
                      How much do you need?{" "}
                      <QuestionHelpIcon
                        details={
                          <span>
                            Amount to be requested <br /> Minimum - {currencyPairCheck(currency)}
                            {formatMoney(
                              isPaymentFinanceForm
                                ? paymentFinanceMinimumAmount
                                : MINIMUM_AMOUNT_TO_USE
                            )} <br />
                            Maximum - {currencyPairCheck(currency)} {formatMoney(MAXIMUM_AMOUNT)}
                          </span>
                        }
                      />
                    </label>
                    <div className="flex items-start gap-2">
                      <div className="bg-secondary-30 px-2 flex items-center justify-center rounded-md h-[56px]">
                        <CurrencyToggle
                          businessId={business?._id}
                          defaultCurrency={currency}
                          forCreditWallet
                          newWalletsData={newWalletsData}
                          isLoadingNewWallets={isLoadingNewWallets}
                          refetchNewWallets={refetchNewWallets}
                          includeCurrencies={
                            isPaymentFinanceForm ?
                              [{ _id: "usdc-fb", currency: "USDC" },
                              { _id: "usdt-fb", currency: "USDT" }]
                              : []
                          }
                          dropDownClassName="shadow-dropdown2 !left-0 !top-[109%]"
                          className="!text-gray-10 !text-base font-light gap-1 flex items-center justify-center w-[115px] !ml-2"
                        />
                      </div>
                      <div className="w-full">
                        <InputField
                          onChange={(value) => {
                            setRequestedAmount(value);
                          }}
                          isValid={inventoryFinanceValidation}
                          value={requestedAmount}
                          showIcon={false}
                          placeholder="Enter amount"
                          wrapperClassName="max-w-full h-[56px]"
                        />
                        {!inventoryFinanceValidation && (
                          <p className="text-xs text-[#C01515] font-normal">
                            This amount is less than the minimum -{" "}
                            {displayCurrencyAndAmount(
                              currency,
                              minimumRequestedAmount
                            )}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                )}

              {paymentMethod == checkFinanceOption("Inventory finance") ? (
                <InventoryFinanceForm
                  productCategory={productCategory}
                  setProductCategory={setProductCategory}
                  productType={productType}
                  newWalletsData={newWalletsData}
                  isLoadingNewWallets={isLoadingNewWallets}
                  refetchNewWallets={refetchNewWallets}
                  setProductType={setProductType}
                  wareHouseLocation={wareHouseLocation}
                  setWareHouseLocation={setWareHouseLocation}
                  province={province}
                  setProvince={setProvince}
                  isBusinessApproved={isBusinessApproved}
                  business={business}
                  setBuyBackAmount={setBuyBackAmount}
                  buyBackAmount={buyBackAmount}
                  isBuyBackValid={isBuyBackValid}
                  maxAmount={maxAmount}
                  buyBackMaxAmount={requestedAmount}
                  buyBackPeriod={buyBackPeriod}
                  setBuyBackPeriod={setBuyBackPeriod}
                />
              ) : paymentMethod == checkFinanceOption("Payment finance") ? (
                    <PaymentFinance
                      network={network}
                      setNetwork={setNetwork}
                      uniqueWallet={uniqueWallet}
                      setUniqueWallet={setUniqueWallet}
                      walletTouched={walletTouched}
                      setWalletTouched={setWalletTouched}
                      isWalletInvalid={isWalletInvalid}
                      swiftRefTouched={swiftRefTouched}
                      setSwiftRefTouched={setSwiftRefTouched}
                      isUetrInvalid={isUetrInvalid}
                      contractAgreement={contractAgreement}
                      setContractAgreement={setContractAgreement}
                      paymentAgreement={paymentAgreement}
                      setPaymentAgreement={setPaymentAgreement}
                      reason={reason}
                      setReason={setReason}
                      duration={duration}
                      setDuration={setDuration}
                      interest={interest}
                      setInterest={setInterest}
                      swiftRef={swiftRef}
                      setSwiftRef={setSwiftRef}
                      selectedToken={selectedToken}
                      setSelectedToken={setSelectedToken}
                      paymentAgreementDoc={paymentAgreementDoc}
                      setPaymentAgreementDoc={setPaymentAgreementDoc}
                    bankName={bankName}
                    setBankName={setBankName}
                    contactPerson={contactPerson}
                    setContactPerson={setContactPerson}
                    contactEmail={contactEmail}
                    setContactEmail={setContactEmail}
                    payoutDestinationType={payoutDestinationType}
                    setPayoutDestinationType={setPayoutDestinationType}
                  />
              ) : (
                <ReceivableFinanceForm
                  paymentMethod={paymentMethod}
                  newWalletsData={newWalletsData}
                  isLoadingNewWallets={isLoadingNewWallets}
                  refetchNewWallets={refetchNewWallets}
                  selectedOption={selectedOption}
                  setSelectedOption={setSelectedOption}
                  orderDimensionsList={orderDimensionsList}
                  handleDelete={handleDelete}
                  setOrderDimensionsList={setOrderDimensionsList}
                  selectedUser={isReceivableForm ? orderData : orderData?.data}
                  setSelectedUser={setSelectedUser}
                  show={show}
                  setShow={setShow}
                  setFileKey={setFileKey}
                  setAddDocModal={setAddDocModal}
                  isBusinessApproved={isBusinessApproved}
                  business={business}
                  setRequestedAmount={setRequestedAmount}
                      isAmountValid={
                        trnxAndFreightSelected
                          ? !isTrnxAndFrieghtValid
                          : isAmountValid
                      }
                  requestedAmount={requestedAmount}
                  maxAmount={maxAmount}
                  transactionDocument={transactionDocument}
                  setTransactionDocument={setTransactionDocument}
                  freightDocument={freightDocument}
                  setFreightDocument={setFreightDocument}
                  contractAgreement={contractAgreement}
                  setContractAgreement={setContractAgreement}
                  businessId={businessId}
                  isLoading={fetchingOrderLoading}
                      currency={currency}
                      isPaymentFinanceForm={isPaymentFinanceForm}
                      paymentFinanceMinimumAmount={paymentFinanceMinimumAmount}
                      trnxAndFreightSelected={trnxAndFreightSelected}
                />
              )}
              {paymentMethod !== checkFinanceOption("Payment finance") && (
                <div className="">
                  <div className="flex items-center gap-1 mb-1">
                    <label className="text-sm text-gray-70">
                      Management fee
                    </label>
                    <QuestionHelpIcon details="A management fee of 1% of the approved amount applies to every approved loan" />
                  </div>
                  <RadioGroup
                    className="flex items-center justify-start gap-4 mt-3"
                    value={selectManagementOption}
                    onValueChange={(value) => setSelectManagementOption(value)}
                  >
                    {managementFinanceOptions.map((option) => (
                      <div
                        className="flex items-center gap-1"
                        key={option.value}
                      >
                        <RadioGroupItem
                          value={option.value}
                          id={option.value}
                        />
                        <label
                          htmlFor={option.value}
                          className="text-sm text-gray-70"
                        >
                          {option.value === "separate" ? `Pay from ${currency} wallet` : option.label}
                        </label>
                      </div>
                    ))}
                  </RadioGroup>

                  <div className="flex items-center gap-3 px-3 py-2 mt-4 bg-[#FBFAFF] rounded-md">
                    <span className="flex items-center justify-center flex-shrink-0 w-4 h-4 text-xs font-bold text-white rounded-full !bg-secondary">
                      !
                    </span>
                    <p className="text-sm text-gray-70">
                      {selectManagementOption === "separate"
                        ? `~${displayCurrencyAndAmount(currency, Number(unformat(String(requestedAmount))) * 0.01)} will be charged to your ${currency} wallet before the loan is disbursed`
                        : `You will receive ~${displayCurrencyAndAmount(currency, Number(unformat(String(requestedAmount))) * 0.99)} when the loan is disbursed`}
                    </p>
                  </div>
                </div>
              )}
            </div>

            <div className="flex items-center justify-end gap-4 mt-3">
              {" "}
              <Button
                variant="ghost"
                onClick={goBack}
                text={"Continue"}
                disabled={false}
              >
                Back
              </Button>
              {isPayableFinanceForm && (
                <GenericBtn
                  onClick={handleSubmit}
                  text={isLoading ? "Loading..." : "Send request"}
                  disabled={
                    hasProcessingFinance
                      ? hasProcessingFinance
                      : isLoading || !isFormValid
                  }
                  hasHover={hasHover}
                />
              )}
              {isReceivableForm && (
                <GenericBtn
                  onClick={handleSubmit}
                  text={isLoading ? "Loading..." : "Send request"}
                  disabled={
                    hasProcessingFinance
                      ? hasProcessingFinance
                      : isLoading || !isPoFormValid
                  }
                  hasHover={hasHover}
                />
              )}
              {isInventoryFinanceForm && (
                <GenericBtn
                  onClick={handleSubmitForInventory}
                  text={
                    (creatingInventoryFinanceLoading ||
                      creatingPaymentFinanceLoading)
                      ? "Loading..."
                      : "Send request"
                  }
                  disabled={
                    hasProcessingFinance
                      ? hasProcessingFinance
                      : creatingInventoryFinanceLoading || !isInventoryFormValid
                  }
                  hasHover={hasHover}
                />
              )}
              {isPaymentFinanceForm && (
                <GenericBtn
                  onClick={handleSubmitForPaymentFinance}
                  text="Send request"
                  disabled={
                    hasProcessingFinance
                      ? hasProcessingFinance
                      : !isPaymentFinanceFormValid
                  }
                  hasHover={hasHover}
                />
              )}
            </div>
            {addDocModal && (
              <UploadDocModal
                close={() => setAddDocModal(null)}
                loading={uploadingDocsLoading}
                uploadFunc={async (selectedFile, name) => {
                  const response = await uploadDocumentFunc({
                    selectedFile,
                    name: selectedFile?.name ?? "",
                  });
                  if (response?.statusCode !== 400) {
                    const file = {
                      size: selectedFile?.size,
                      name: selectedFile?.name ?? "",
                      url: response?.link,
                    };
                    addFilesToState(addDocModal, file);
                    setAddDocModal(null);
                  }
                }}
              />
            )}
          </section>
        </section>
      </main>
      <SuccessModal
        show={showSuccess}
        close={() => setShowSuccessModal(false)}
        onButtonClick={() => navigate("/finance/credit")}
        linkText="Back to dashboard"
        text={
          isDraft
            ? "This finance was created as a draft"
            : "You should receive an offer in 2-10 days"
        }
        title={isDraft ? "Finance created as draft" : "Finance request sent"}
      />
    </>
  );
};

export default PayableFinance;
